import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Avatar from 'react-avatar';
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import ElipsisText from '../../../../../components/ElipsisText';
import ElipsisCell from '../../../../../components/TableBox/Cells/EllipsisCell';
import Link from '../../../../../components/Link';
import Badge from '../../../../../components/Badge';
import Button from '../../../../../components/Button';
import {
  RowClassName,
  Modules,
  AuthRoutes,
  RouteParamsTypes,
  PermissionModulesEnum,
  PermissionActionsEnum,
} from '../../../../../enums';
import { TableContext } from '../../../../../contexts/TableContext';
import { AuthContext } from '../../../../../contexts/AuthContext';
import { hasResourseAccess } from '../../../../../utils/methods';
import { UserResponse } from '../../../../../interfaces/user.interface';

interface RowProps {
  item: UserResponse;
  className?: RowClassName;
}

const Row: React.FC<RowProps> = ({ item, className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { headCells } = useContext(TableContext);
  const { isSystemAdmin, permissions } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.Users]: [PermissionActionsEnum.Update] }],
  });

  const handleEditButtonClick = (id: string | number) => {
    navigate(`${Modules.resources}${AuthRoutes.users}${RouteParamsTypes.edit}/${id}`);
  };
  const handleViewClick = (id: string | number) => {
    navigate(`${Modules.resources}${AuthRoutes.users}${RouteParamsTypes.view}/${id}`);
  };

  const fullName = [item.salutation?.value, item.first_name, item.last_name]
    .filter(Boolean)
    .join(' ');

  const userNode = (
    <div className="d-flex align-items-center">
      <div className="me-2" style={{ width: 42 }}>
        <Avatar name={fullName || '-'} size="40" maxInitials={2} round />
      </div>
      <div style={{ width: 'calc(100% - 52px)' }}>
        <ElipsisText
          width="100%"
          text={fullName || '-'}
          node={<Link text={fullName || '-'} />}
          className="h6 mb-1 text-decoration-underline hyperlink"
          onClick={() => handleViewClick(item.id)}
        />
        <ElipsisText width="100%" text={item.email || '-'} />
      </div>
    </div>
  );

  const updatedNode = (
    <div>
      <div className="d-block text-truncate" style={{ maxWidth: '150px' }}>
        {item.modify_by?.first_name} {item.modify_by?.last_name}
      </div>
      <ElipsisText width="100%" text={item.modify_date ? new Date(item.modify_date).toLocaleDateString() : '-'} />
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.first_name) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={userNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.role) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item.role?.value || '-'}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Badge
                text={
                  item.is_active
                    ? t('users.list.pageTexts.table.status.active')
                    : t('users.list.pageTexts.table.status.inActive')
                }
                variant={item.is_active ? 'success' : 'danger'}
              />
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.updated_at) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={updatedNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex align-items-sm-center justify-content-sm-start">
                {hasEditAccess ? (
                  <Button
                    className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                    icon={<i className="ri-edit-box-line ri-20px"></i>}
                    onClick={() => handleEditButtonClick(item.id)}
                  />
                ) : (
                  <Button
                    className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                    icon={<i className="ri-eye-line ri-20px"></i>}
                    onClick={() => handleViewClick(item.id)}
                  />
                )}
              </div>
            </td>
          );
        }

        const value = item[headCell.id as keyof UserResponse];
        return (
          <ElipsisCell key={headCell.id} width={headCell.width} text={value ? String(value) : '-'} />
        );
      })}
    </tr>
  );
};

export default Row;
